import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderOpen,
  Target,
  FileText,
  CheckCircle,
  Trophy,
  Gift,
  Package,
  Users,
  Menu,
  UserCircle,
  UserPlus,
  LogOut,
  X,
  Building2,
  Bell,
  HelpCircle,
} from 'lucide-react';
import NotificationPanel from './NotificationPanel';
import '../styles/notifications.css';
import { User } from '../utils/auth';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePointsEnabled } from '../hooks/usePointsEnabled';
import '../styles/sidebar.css';

interface SidebarProps {
  currentUser: User | null;
  onInviteUser: () => void;
  onLogout: () => void;
  onSupport: () => void;
}

export default function Sidebar({ currentUser, onInviteUser, onLogout, onSupport }: SidebarProps) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingClaimsCount, setPendingClaimsCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const isEmployee = currentUser?.role === 'employee';
  const pointsEnabled = usePointsEnabled();

  useEffect(() => {
    const refresh = () => {
      fetchUnreadNotificationCount();
      if (isAdmin) {
        fetchPendingCount();
        fetchPendingClaimsCount();
      }
    };

    refresh();
    const interval = setInterval(refresh, 30000);
    window.addEventListener('crewcast:refresh-admin-counts', refresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('crewcast:refresh-admin-counts', refresh);
    };
  }, [isAdmin]);

  const fetchPendingCount = async () => {
    try {
      if (!token) return;

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/submissions/pending`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingCount(data.submissions.length);
      }
    } catch (err) {
      console.error('Failed to fetch pending count:', err);
    }
  };

  const fetchPendingClaimsCount = async () => {
    try {
      if (!token) return;

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/claimed-rewards?status=claimed`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingClaimsCount(data.claimedRewards.length);
      }
    } catch (err) {
      console.error('Failed to fetch pending claims count:', err);
    }
  };

  const fetchUnreadNotificationCount = async () => {
    try {
      if (!token) return;

      const response = await fetch(`${authConfig.apiUrl}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadNotificationCount(data.count ?? 0);
      }
    } catch (err) {
      console.error('Failed to fetch unread notification count:', err);
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const navTo = (path: string) => {
    navigate(path);
    closeMobileMenu();
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-content">
        {/* Logo/Brand */}
        <div className="sidebar-header">
          <img
            src="/crewcast-logo-white.svg"
            alt="CrewCast"
            className="sidebar-brand-logo"
          />
          <button
            className={`notification-bell-btn ${notificationPanelOpen ? 'active' : ''}`}
            onClick={() => setNotificationPanelOpen((o) => !o)}
            aria-label="Notifications"
          >
            <Bell size={20} />
            {unreadNotificationCount > 0 && (
              <span className="notification-bell-badge">
                {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
              </span>
            )}
          </button>
        </div>

        {/* Navigation Links (Desktop) */}
        <nav className="sidebar-nav desktop-nav">
          {/* 1. Dashboard */}
          <button
            className={`sidebar-nav-item ${isActive('/dashboard') ? 'active' : ''}`}
            onClick={() => navigate('/dashboard')}
          >
            <span className="sidebar-nav-icon">
              <LayoutDashboard size={20} />
            </span>
            <span className="sidebar-nav-text">Dashboard</span>
          </button>

          {/* 2. Content Vault - only visible to admins */}
          {isAdmin && (
            <button
              className={`sidebar-nav-item ${isActive('/content-vault') ? 'active' : ''}`}
              onClick={() => navigate('/content-vault')}
            >
              <span className="sidebar-nav-icon">
                <FolderOpen size={20} />
              </span>
              <span className="sidebar-nav-text">Content Vault</span>
            </button>
          )}

          {/* 3. Moments - visible to all users */}
          <button
            className={`sidebar-nav-item ${location.pathname.startsWith('/moments') ? 'active' : ''}`}
            onClick={() => navigate('/moments')}
          >
            <span className="sidebar-nav-icon">
              <Target size={20} />
            </span>
            <span className="sidebar-nav-text">Moments</span>
          </button>

          {/* My Submissions - only visible to employees */}
          {currentUser?.role === 'employee' && (
            <button
              className={`sidebar-nav-item ${location.pathname.startsWith('/submissions') ? 'active' : ''}`}
              onClick={() => navigate('/submissions')}
            >
              <span className="sidebar-nav-icon">
                <FileText size={20} />
              </span>
              <span className="sidebar-nav-text">My Submissions</span>
            </button>
          )}

          {/* 4. Review Submissions - only visible to admins */}
          {isAdmin && (
            <button
              className={`sidebar-nav-item ${isActive('/review-submissions') ? 'active' : ''}`}
              onClick={() => navigate('/review-submissions')}
            >
              <span className="sidebar-nav-icon">
                <CheckCircle size={20} />
              </span>
              <span className="sidebar-nav-text">Review Submissions</span>
              {pendingCount > 0 && (
                <span className="pending-count">{pendingCount}</span>
              )}
            </button>
          )}

          {/* 5. Rewards - always visible to admins; employees only see when pointsEnabled */}
          {(isAdmin || pointsEnabled) && (
            <button
              className={`sidebar-nav-item ${location.pathname.startsWith('/rewards') ? 'active' : ''}`}
              onClick={() => navigate('/rewards')}
            >
              <span className="sidebar-nav-icon">
                <Trophy size={20} />
              </span>
              <span className="sidebar-nav-text">Rewards</span>
            </button>
          )}

          {/* My Claimed Rewards - visible to employees only when pointsEnabled */}
          {isEmployee && pointsEnabled && (
            <button
              className={`sidebar-nav-item ${isActive('/claimed-rewards') ? 'active' : ''}`}
              onClick={() => navigate('/claimed-rewards')}
            >
              <span className="sidebar-nav-icon">
                <Gift size={20} />
              </span>
              <span className="sidebar-nav-text">My Claimed Rewards</span>
            </button>
          )}

          {/* 6. Claimed Rewards - admin review */}
          {isAdmin && (
            <button
              className={`sidebar-nav-item ${location.pathname.startsWith('/admin/claimed-rewards') ? 'active' : ''}`}
              onClick={() => navigate('/admin/claimed-rewards')}
            >
              <span className="sidebar-nav-icon">
                <Package size={20} />
              </span>
              <span className="sidebar-nav-text">Claimed Rewards</span>
              {pendingClaimsCount > 0 && (
                <span className="pending-count">{pendingClaimsCount}</span>
              )}
            </button>
          )}

          {/* 7. Employees - only visible to admins */}
          {isAdmin && (
            <button
              className={`sidebar-nav-item ${isActive('/employees') ? 'active' : ''}`}
              onClick={() => navigate('/employees')}
            >
              <span className="sidebar-nav-icon">
                <Users size={20} />
              </span>
              <span className="sidebar-nav-text">Employees</span>
            </button>
          )}

          {/* Back Office - superadmin only */}
          {currentUser?.role === 'superadmin' && (
            <button
              className={`sidebar-nav-item ${location.pathname.startsWith('/backoffice') ? 'active' : ''}`}
              onClick={() => navigate('/backoffice')}
            >
              <span className="sidebar-nav-icon">
                <Building2 size={20} />
              </span>
              <span className="sidebar-nav-text">Back Office</span>
            </button>
          )}
        </nav>

        {/* Navigation Links (Mobile bottom bar) */}
        <nav className="sidebar-nav mobile-nav" aria-label="Bottom navigation">
          <button
            className={`sidebar-nav-item ${isActive('/dashboard') ? 'active' : ''}`}
            onClick={() => navTo('/dashboard')}
          >
            <span className="sidebar-nav-icon">
              <LayoutDashboard size={20} />
            </span>
            <span className="sidebar-nav-text">Dashboard</span>
          </button>

          {/* Show Content Vault for admins; Rewards for employees when on, My Submissions when off */}
          {isAdmin ? (
            <button
              className={`sidebar-nav-item ${isActive('/content-vault') ? 'active' : ''}`}
              onClick={() => navTo('/content-vault')}
            >
              <span className="sidebar-nav-icon">
                <FolderOpen size={20} />
              </span>
              <span className="sidebar-nav-text">Content Vault</span>
            </button>
          ) : pointsEnabled ? (
            <button
              className={`sidebar-nav-item ${location.pathname.startsWith('/rewards') ? 'active' : ''}`}
              onClick={() => navTo('/rewards')}
            >
              <span className="sidebar-nav-icon">
                <Trophy size={20} />
              </span>
              <span className="sidebar-nav-text">Rewards</span>
            </button>
          ) : (
            <button
              className={`sidebar-nav-item ${location.pathname.startsWith('/submissions') ? 'active' : ''}`}
              onClick={() => navTo('/submissions')}
            >
              <span className="sidebar-nav-icon">
                <FileText size={20} />
              </span>
              <span className="sidebar-nav-text">My Submissions</span>
            </button>
          )}

          <button
            className={`sidebar-nav-item ${location.pathname.startsWith('/moments') ? 'active' : ''}`}
            onClick={() => navTo('/moments')}
          >
            <span className="sidebar-nav-icon">
              <Target size={20} />
            </span>
            <span className="sidebar-nav-text">Moments</span>
          </button>

          <button
            className={`sidebar-nav-item ${notificationPanelOpen ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              setNotificationPanelOpen((o) => !o);
            }}
            aria-label="Notifications"
          >
            <span className="sidebar-nav-icon" style={{ position: 'relative' }}>
              <Bell size={20} />
              {unreadNotificationCount > 0 && (
                <span className="notification-bell-badge mobile-nav-badge">
                  {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                </span>
              )}
            </span>
            <span className="sidebar-nav-text">Notifications</span>
          </button>

          <button
            className={`sidebar-nav-item ${mobileMenuOpen ? 'active' : ''}`}
            onClick={() => {
              setNotificationPanelOpen(false);
              setMobileMenuOpen((o) => !o);
            }}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu-sheet"
          >
            <span className="sidebar-nav-icon">
              <Menu size={20} />
            </span>
            <span className="sidebar-nav-text">More</span>
          </button>
        </nav>

        {/* Points Display - only for employees when pointsEnabled */}
        {isEmployee && pointsEnabled && (
          <div className="sidebar-points">
            <div className="sidebar-points-icon">
              <Trophy size={24} />
            </div>
            <div className="sidebar-points-info">
              <div className="sidebar-points-label">Your Points</div>
              <div className="sidebar-points-value">{currentUser.points || 0}</div>
            </div>
          </div>
        )}

        {/* Bottom Section - User & Actions */}
        <div className="sidebar-footer">
          {/* Admin Action */}
          {isAdmin && (
            <button onClick={onInviteUser} className="sidebar-action-btn">
              <span className="sidebar-action-icon">
                <UserPlus size={20} />
              </span>
              <span className="sidebar-action-text">Invite User</span>
            </button>
          )}

          {/* User Info - Clickable to navigate to profile */}
          {currentUser && (
            <button 
              className="sidebar-user"
              onClick={() => navigate('/profile')}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
                padding: '16px',
                borderRadius: '12px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div className="sidebar-user-avatar">
                  {currentUser.profilePicture ? (
                    <img 
                      src={currentUser.profilePicture} 
                      alt={`${currentUser.firstName} ${currentUser.lastName}`}
                      className="sidebar-user-avatar-img"
                    />
                  ) : (
                    currentUser.firstName?.[0]?.toUpperCase() || '?'
                  )}
                </div>
                <div className="sidebar-user-info">
                  <div className="sidebar-user-name">
                    {currentUser.firstName} {currentUser.lastName}
                  </div>
                  <div className="sidebar-user-role">
                    {currentUser.role}
                  </div>
                  <div className="sidebar-user-company">
                    {currentUser.companyName ?? currentUser.companyId}
                  </div>
                </div>
              </div>
            </button>
          )}

          {/* Help / Support */}
          <button onClick={onSupport} className="sidebar-action-btn">
            <span className="sidebar-action-icon">
              <HelpCircle size={20} />
            </span>
            <span className="sidebar-action-text">Help</span>
          </button>

          {/* Logout Button */}
          <button onClick={onLogout} className="sidebar-logout-btn">
            <span className="sidebar-logout-icon">
              <LogOut size={20} />
            </span>
            <span className="sidebar-logout-text">Logout</span>
          </button>
        </div>
      </div>

      {/* Notification Panel */}
      {notificationPanelOpen && (
        <NotificationPanel
          onClose={() => setNotificationPanelOpen(false)}
          onUnreadCountChange={setUnreadNotificationCount}
        />
      )}

      {/* Mobile slide-up menu */}
      {mobileMenuOpen && (
        <>
          <div className="mobile-menu-overlay" onClick={closeMobileMenu} />
          <div
            className="mobile-menu-sheet"
            id="mobile-menu-sheet"
            role="dialog"
            aria-modal="true"
          >
            <div className="mobile-menu-header">
              <div className="mobile-menu-title">Menu</div>
              <button
                className="mobile-menu-close"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mobile-menu-list">
              <button className="mobile-menu-item" onClick={() => navTo('/profile')}>
                <span className="mobile-menu-icon">
                  <UserCircle size={20} />
                </span>
                <span>Profile</span>
              </button>

              {isEmployee && (
                <>
                  <button className="mobile-menu-item" onClick={() => navTo('/submissions')}>
                    <span className="mobile-menu-icon">
                      <FileText size={20} />
                    </span>
                    <span>My Submissions</span>
                  </button>
                  {pointsEnabled && (
                    <button className="mobile-menu-item" onClick={() => navTo('/claimed-rewards')}>
                      <span className="mobile-menu-icon">
                        <Gift size={20} />
                      </span>
                      <span>My Claimed Rewards</span>
                    </button>
                  )}
                </>
              )}

              {isAdmin && (
                <>
                  {/* Review Submissions (not in bottom bar) */}
                  <button className="mobile-menu-item" onClick={() => navTo('/review-submissions')}>
                    <span className="mobile-menu-icon">
                      <CheckCircle size={20} />
                    </span>
                    <span>Review Submissions</span>
                    {pendingCount > 0 && <span className="mobile-menu-badge">{pendingCount}</span>}
                  </button>
                  {/* Rewards (not in bottom bar for admins) */}
                  <button className="mobile-menu-item" onClick={() => navTo('/rewards')}>
                    <span className="mobile-menu-icon">
                      <Trophy size={20} />
                    </span>
                    <span>Rewards</span>
                  </button>
                  {/* Claimed Rewards */}
                  <button className="mobile-menu-item" onClick={() => navTo('/admin/claimed-rewards')}>
                    <span className="mobile-menu-icon">
                      <Package size={20} />
                    </span>
                    <span>Claimed Rewards</span>
                    {pendingClaimsCount > 0 && <span className="mobile-menu-badge">{pendingClaimsCount}</span>}
                  </button>
                  {/* Employees */}
                  <button className="mobile-menu-item" onClick={() => navTo('/employees')}>
                    <span className="mobile-menu-icon">
                      <Users size={20} />
                    </span>
                    <span>Employees</span>
                  </button>
                  {/* Invite User */}
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      closeMobileMenu();
                      onInviteUser();
                    }}
                  >
                    <span className="mobile-menu-icon">
                      <UserPlus size={20} />
                    </span>
                    <span>Invite User</span>
                  </button>
                </>
              )}

              {currentUser?.role === 'superadmin' && (
                <button className="mobile-menu-item" onClick={() => navTo('/backoffice')}>
                  <span className="mobile-menu-icon">
                    <Building2 size={20} />
                  </span>
                  <span>Back Office</span>
                </button>
              )}

              {isEmployee && pointsEnabled && (
                <div className="mobile-menu-points">
                  <Trophy size={20} />
                  <span>Your Points</span>
                  <strong>{currentUser!.points || 0}</strong>
                </div>
              )}

              <button
                className="mobile-menu-item"
                onClick={() => {
                  closeMobileMenu();
                  onSupport();
                }}
              >
                <span className="mobile-menu-icon">
                  <HelpCircle size={20} />
                </span>
                <span>Help</span>
              </button>

              <button className="mobile-menu-item danger" onClick={onLogout}>
                <span className="mobile-menu-icon">
                  <LogOut size={20} />
                </span>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </>
      )}
    </aside>
  );
}

